import { useEffect, useState } from "react";
import { Plus, X, Gauge, Activity } from "lucide-react";
import { api } from "../../lib/api";

/**
 * RomEditor — Iter AB (rewrite)
 *
 * Each ROM row is one of three modes:
 *   - 'restriction' (default for Quick mode): 4-tier scale before + after
 *       normal | mild | moderate | severe
 *   - 'comparison'  (alt for Quick mode): single 3-tier tap
 *       improved | unchanged | worse
 *   - 'degree'      (Degree mode): numeric before/after/target with %-gain pill
 *
 * Therapists toggle a global mode at the top (Quick vs Degree) which decides
 * what new rows default to, but EVERY row exposes a per-row mode pill so the
 * therapist can mix-and-match (e.g. quick restriction for shoulder, degree for
 * cervical rotation). This is the user's chosen "option c".
 *
 * Backend shape (RomMeasurement):
 *   { label, mode, before?, after?, target?, quality_before?, quality_after?, comparison?, note? }
 */

const RESTRICTION_OPTIONS = [
  { value: "normal",   label: "Normal",   shortLabel: "Normal",   tone: "sage" },
  { value: "mild",     label: "Mild",     shortLabel: "Mild",     tone: "amber" },
  { value: "moderate", label: "Moderate", shortLabel: "Mod",      tone: "amber-dark" },
  { value: "severe",   label: "Severe",   shortLabel: "Severe",   tone: "rose" },
];

const COMPARISON_OPTIONS = [
  { value: "improved",  label: "Improved",  tone: "sage" },
  { value: "unchanged", label: "Unchanged", tone: "amber" },
  { value: "worse",     label: "Worse",     tone: "rose" },
];

const TONE_CLASSES = {
  sage:       "bg-sage-500/15 text-sage-800 border-sage-500/30",
  amber:      "bg-amber-100 text-amber-800 border-amber-200",
  "amber-dark":"bg-amber-200 text-amber-900 border-amber-300",
  rose:       "bg-rose-100 text-rose-800 border-rose-300",
  off:        "bg-white text-ink-700 border-paper-200 hover:border-ink-900/30",
};

function pctImprovement(b, a) {
  if (b == null || a == null || isNaN(b) || isNaN(a)) return null;
  if (Number(b) <= 0) return null;
  return Math.round(((a - b) / b) * 100);
}

const RESTRICTION_RANK = { normal: 0, mild: 1, moderate: 2, severe: 3 };
function restrictionDelta(qb, qa) {
  if (!qb || !qa) return null;
  const b = RESTRICTION_RANK[qb], a = RESTRICTION_RANK[qa];
  if (b == null || a == null) return null;
  return b - a; // positive = improved
}

export default function RomEditor({ value = [], onChange }) {
  const [showPresets, setShowPresets] = useState(false);
  const [globalMode, setGlobalMode] = useState("restriction"); // 'restriction' (Quick) | 'degree'
  const [presets, setPresets] = useState([]);

  // Pull degree-mode presets from the backend so adding new movements doesn't
  // require a frontend redeploy.
  useEffect(() => {
    api.get("/rom/presets").then((r) => setPresets(r.data.rom_presets || [])).catch(() => {});
  }, []);

  const usedLabels = new Set((value || []).map((m) => m.label));
  const availablePresets = presets.filter((p) => !usedLabels.has(p.label));

  const add = (preset) => {
    const base = {
      label: preset?.label || "",
      mode: globalMode,
      // degree fields
      before: null, after: null,
      target: preset?.degrees_normal ?? null,
      // restriction fields
      quality_before: null, quality_after: null,
      // comparison
      comparison: null,
      note: "",
    };
    onChange([...(value || []), base]);
    setShowPresets(false);
  };

  const update = (i, patch) => {
    const next = (value || []).map((m, idx) => (idx === i ? { ...m, ...patch } : m));
    onChange(next);
  };

  const remove = (i) => onChange((value || []).filter((_, idx) => idx !== i));

  return (
    <div data-testid="rom-editor">
      <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="label-overline">Range of motion (optional)</div>
          <p className="text-xs text-ink-500 mt-0.5">Quick restriction scale by default · switch to Degree for measurable ROM · mix per row.</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Global mode toggle */}
          <div className="inline-flex bg-paper-100 rounded-full p-0.5" data-testid="rom-global-mode">
            <ModeChip
              active={globalMode === "restriction"}
              onClick={() => setGlobalMode("restriction")}
              icon={Activity}
              label="Quick"
              testid="rom-global-mode-restriction"
            />
            <ModeChip
              active={globalMode === "degree"}
              onClick={() => setGlobalMode("degree")}
              icon={Gauge}
              label="Degree"
              testid="rom-global-mode-degree"
            />
          </div>
          {/* Add */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowPresets((v) => !v)}
              className="min-h-[36px] px-3 rounded-full border border-paper-200 text-xs text-ink-700 hover:bg-paper-50 inline-flex items-center gap-1.5"
              data-testid="rom-add-btn"
            >
              <Plus className="w-3.5 h-3.5" /> Add measurement
            </button>
            {showPresets && (
              <div
                className="absolute right-0 mt-1 w-[min(20rem,calc(100vw-2rem))] bg-white border border-paper-200 rounded-2xl shadow-lg z-10 p-1.5 max-h-80 overflow-y-auto"
                data-testid="rom-preset-menu"
              >
                {availablePresets.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => add(p)}
                    className="w-full text-left text-sm px-3 py-2 rounded-xl hover:bg-paper-50 flex items-center justify-between"
                    data-testid={`rom-preset-${p.id}`}
                  >
                    <span>{p.label}</span>
                    <span className="text-[10px] uppercase tracking-wider text-ink-500">{p.degrees_normal}° normal</span>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => add(null)}
                  className="w-full text-left text-sm px-3 py-2 rounded-xl hover:bg-paper-50 text-sage-700 border-t border-paper-100 mt-1"
                  data-testid="rom-add-custom"
                >
                  + Custom measurement…
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {(value || []).length === 0 ? (
        <div className="text-sm text-ink-500 bg-paper-50 border border-paper-100 rounded-2xl px-4 py-3" data-testid="rom-empty">
          Add joint / movement measurements to track range-of-motion gains. They appear on the progress report.
        </div>
      ) : (
        <div className="space-y-2">
          {value.map((m, i) => (
            <RomRow
              key={i}
              row={m}
              index={i}
              onChange={(patch) => update(i, patch)}
              onRemove={() => remove(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ModeChip({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      data-active={active}
      className={`min-h-[32px] px-3 rounded-full text-xs font-medium inline-flex items-center gap-1 transition-colors ${
        active ? "bg-white text-ink-900 shadow-sm" : "text-ink-600 hover:text-ink-900"
      }`}
      aria-pressed={active}
    >
      <Icon className="w-3 h-3" />
      {label}
    </button>
  );
}

function RomRow({ row, index, onChange, onRemove }) {
  const m = row || {};
  const mode = m.mode || "degree";
  return (
    <div className="bg-paper-50 border border-paper-200 rounded-2xl p-3 sm:p-4" data-testid={`rom-row-${index}`} data-mode={mode}>
      <div className="flex items-start gap-2 flex-wrap mb-2">
        <input
          value={m.label || ""}
          onChange={(e) => onChange({ label: e.target.value })}
          placeholder="e.g. Cervical rotation (L)"
          data-testid={`rom-label-${index}`}
          className="flex-1 min-w-[160px] min-h-[36px] px-3 rounded-xl bg-white border border-paper-200 outline-none text-sm"
        />

        {/* Per-row mode picker */}
        <div className="inline-flex bg-white border border-paper-200 rounded-full p-0.5" data-testid={`rom-row-mode-${index}`}>
          <RowModeBtn
            active={mode === "restriction"}
            onClick={() => onChange({ mode: "restriction" })}
            label="Quick"
            testid={`rom-row-mode-restriction-${index}`}
          />
          <RowModeBtn
            active={mode === "comparison"}
            onClick={() => onChange({ mode: "comparison" })}
            label="±"
            testid={`rom-row-mode-comparison-${index}`}
          />
          <RowModeBtn
            active={mode === "degree"}
            onClick={() => onChange({ mode: "degree" })}
            label="°"
            testid={`rom-row-mode-degree-${index}`}
          />
        </div>

        <button
          type="button"
          onClick={onRemove}
          className="w-8 h-8 rounded-full text-ink-400 hover:bg-paper-200 inline-flex items-center justify-center"
          data-testid={`rom-remove-${index}`}
          aria-label="Remove measurement"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {mode === "restriction" && (
        <RestrictionFields row={m} index={index} onChange={onChange} />
      )}
      {mode === "comparison" && (
        <ComparisonField row={m} index={index} onChange={onChange} />
      )}
      {mode === "degree" && (
        <DegreeFields row={m} index={index} onChange={onChange} />
      )}
    </div>
  );
}

function RowModeBtn({ active, onClick, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      data-active={active}
      className={`min-h-[26px] px-2 rounded-full text-[11px] font-semibold transition-colors ${
        active ? "bg-ink-900 text-white" : "text-ink-600 hover:text-ink-900"
      }`}
      aria-pressed={active}
    >
      {label}
    </button>
  );
}

function RestrictionFields({ row, index, onChange }) {
  const delta = restrictionDelta(row.quality_before, row.quality_after);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`rom-restriction-${index}`}>
      <RestrictionPicker
        label="Before"
        value={row.quality_before}
        onChange={(q) => onChange({ quality_before: q })}
        testidPrefix={`rom-restriction-before-${index}`}
      />
      <RestrictionPicker
        label="After"
        value={row.quality_after}
        onChange={(q) => onChange({ quality_after: q })}
        testidPrefix={`rom-restriction-after-${index}`}
      />
      {delta !== null && delta !== 0 && (
        <div className="sm:col-span-2 text-xs" data-testid={`rom-restriction-delta-${index}`}>
          <span className={`inline-block px-2 py-0.5 rounded-full font-medium ${
            delta > 0 ? "bg-sage-500/15 text-sage-800" : "bg-rose-100 text-rose-800"
          }`}>
            {delta > 0 ? `Improved ${delta} level${delta === 1 ? "" : "s"}` : `Worsened ${Math.abs(delta)} level${Math.abs(delta) === 1 ? "" : "s"}`}
          </span>
        </div>
      )}
    </div>
  );
}

function RestrictionPicker({ label, value, onChange, testidPrefix }) {
  return (
    <div>
      <div className="label-overline mb-1">{label}</div>
      <div className="grid grid-cols-4 gap-1">
        {RESTRICTION_OPTIONS.map((opt) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(active ? null : opt.value)}
              data-testid={`${testidPrefix}-${opt.value}`}
              data-active={active}
              className={`min-h-[34px] px-1 rounded-lg text-[11px] font-medium border transition-colors ${
                active ? TONE_CLASSES[opt.tone] : TONE_CLASSES.off
              }`}
              aria-pressed={active}
            >
              {opt.shortLabel}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ComparisonField({ row, index, onChange }) {
  return (
    <div className="grid grid-cols-3 gap-1.5" data-testid={`rom-comparison-${index}`}>
      {COMPARISON_OPTIONS.map((opt) => {
        const active = row.comparison === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange({ comparison: active ? null : opt.value })}
            data-testid={`rom-comparison-${opt.value}-${index}`}
            data-active={active}
            className={`min-h-[40px] px-2 rounded-xl text-sm font-medium border transition-colors ${
              active ? TONE_CLASSES[opt.tone] : TONE_CLASSES.off
            }`}
            aria-pressed={active}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function DegreeFields({ row, index, onChange }) {
  const pct = pctImprovement(row.before, row.after);
  const targetGap = row.target != null && row.after != null ? Math.max(0, row.target - row.after) : null;
  const normal = row.target || null;
  const percentOfNormal = normal && row.after != null ? Math.round((row.after / normal) * 100) : null;
  return (
    <div data-testid={`rom-degree-${index}`}>
      <div className="grid grid-cols-3 gap-2">
        <DegreeInput
          label="Before °"
          value={row.before}
          onChange={(v) => onChange({ before: v })}
          testid={`rom-before-${index}`}
        />
        <DegreeInput
          label="After °"
          value={row.after}
          onChange={(v) => onChange({ after: v })}
          testid={`rom-after-${index}`}
        />
        <DegreeInput
          label="Target °"
          value={row.target}
          onChange={(v) => onChange({ target: v })}
          testid={`rom-target-${index}`}
        />
      </div>
      {/* Visual slider for After against target */}
      {normal && (
        <div className="mt-3" data-testid={`rom-slider-${index}`}>
          <input
            type="range"
            min={0}
            max={Math.max(normal, 180)}
            step={1}
            value={row.after ?? 0}
            onChange={(e) => onChange({ after: parseInt(e.target.value, 10) })}
            className="w-full"
            aria-label="After ROM degrees"
          />
          <div className="flex justify-between text-[10px] text-ink-500 mt-1">
            <span>0°</span>
            <span className="font-medium text-ink-900">Normal: {normal}°</span>
            <span>{Math.max(normal, 180)}°</span>
          </div>
        </div>
      )}
      {(pct !== null || percentOfNormal !== null) && (
        <div className="mt-2 text-xs flex items-center gap-2 flex-wrap" data-testid={`rom-degree-stats-${index}`}>
          {pct !== null && (
            <span className={`px-2 py-0.5 rounded-full font-medium ${
              pct > 0 ? "bg-sage-500/10 text-sage-700" :
              pct === 0 ? "bg-paper-200 text-ink-600" :
              "bg-terracotta-500/10 text-terracotta-600"
            }`}>
              {pct > 0 ? `+${pct}% range` : pct === 0 ? "Holding steady" : `${pct}%`}
            </span>
          )}
          {percentOfNormal !== null && (
            <span className="text-ink-500">{percentOfNormal}% of normal</span>
          )}
          {targetGap !== null && row.target != null && row.after != null && (
            <span className="text-ink-500">
              {row.after >= row.target ? "✓ Reached target" : `${targetGap}° from target`}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function DegreeInput({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="label-overline block mb-1">{label}</label>
      <input
        type="number"
        min={0}
        max={300}
        value={value == null ? "" : value}
        onChange={(e) => onChange(e.target.value === "" ? null : Math.max(0, Math.min(300, parseInt(e.target.value, 10) || 0)))}
        data-testid={testid}
        className="w-full min-h-[40px] px-3 rounded-xl bg-white border border-paper-200 outline-none text-sm"
      />
    </div>
  );
}
